import type { HoldType } from '../../../types/hold-type';

interface HoldTypeSwatchProps {
  type: Pick<HoldType, 'name' | 'color' | 'color2'>;
  className?: string;
}

/**
 * The colours a hold type carries, as one dot — split down the middle when the
 * gym gave two, which is how a two-tone hold is actually described ("bianco e
 * nero").
 *
 * Nothing at all where the gym has named a type without colouring it: an empty
 * grey circle beside every row would read as "this one has no holds" rather than
 * as "nobody has said yet".
 */
export function HoldTypeSwatch({ type, className = '' }: HoldTypeSwatchProps) {
  const colors = [type.color, type.color2].filter((color): color is string => Boolean(color));
  if (colors.length === 0) return null;

  const background =
    colors.length === 1
      ? colors[0]
      : `linear-gradient(90deg, ${colors[0]} 0 50%, ${colors[1]} 50% 100%)`;

  return (
    // Decorative: the type's name is always right beside it, and letting the
    // dot into the accessible name would read every row out twice.
    <span
      data-testid="hold-type-swatch"
      aria-hidden
      title={type.name}
      className={`inline-block size-3.5 shrink-0 rounded-full border border-border ${className}`}
      style={{ background }}
    />
  );
}
